import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import FormLabel from '@mui/joy/FormLabel';
import FormHelperText from '@mui/joy/FormHelperText';
import Autocomplete from '@mui/joy/Autocomplete';
import CircularProgress from '@mui/joy/CircularProgress';
import Button from '@mui/joy/Button';
import _ from 'lodash';
import { getShipmentTypes, getShipmentType } from '../API/shipmentType';
import { getPaginatedResults } from '../utils/pagination';
import { PERMISSIONS } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';

const ALL_OPTION_ID = '__all__';

function resolveDefaultValueId(defaultValue) {
  if (defaultValue == null || defaultValue === '') {
    return null;
  }
  if (typeof defaultValue === 'object' && defaultValue.id != null) {
    return defaultValue.id;
  }
  return defaultValue;
}

function makeAllOption(t) {
  return { id: ALL_OPTION_ID, name: t('actions.all'), __isAll: true };
}

function labelForType(option) {
  if (!option) return '';
  if (option.__isAll) return option.name;
  return option.name || '';
}

function isAllOption(option) {
  return option != null && (option.__isAll || option.id === ALL_OPTION_ID);
}

export default function ShipmentTypeOptionList({
  onChange,
  required,
  defaultValue,
  disabled = false,
  label,
  showAll = false,
  size,
  sx: sxProp = {},
  slotProps = {},
  errorMessage,
}) {
  const { t } = useTranslation('ui');
  const { hasPermission } = usePermissions();
  const canListShipmentTypes = hasPermission(PERMISSIONS.SHIPMENT_TYPES_LIST);
  const resolvedLabel = label ?? t('widgets.shipmentType');
  const allOption = useMemo(() => makeAllOption(t), [t]);
  const defaultId = resolveDefaultValueId(defaultValue);

  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);

  const handleInputChange = (event, newInputValue) => {
    setInputValue(newInputValue);
  };

  const fetchTypes = async (search) => {
    if (!canListShipmentTypes) {
      setOptions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await getShipmentTypes({
        page: 1,
        page_size: 30,
        search: search || '',
      });
      const results = getPaginatedResults(response?.data);
      const nextOptions = showAll ? [allOption, ...results] : results;
      setOptions(nextOptions);
    } catch (err) {
      console.error('ShipmentTypeOptionList:', err);
      setOptions(showAll ? [allOption] : []);
    } finally {
      setLoading(false);
    }
  };

  const debouncedFetch = useCallback(_.debounce(fetchTypes, 300), [showAll, allOption, canListShipmentTypes]);

  useEffect(() => {
    if (inputValue) {
      debouncedFetch(inputValue);
    } else {
      setOptions(showAll ? [allOption] : []);
    }
  }, [inputValue, debouncedFetch, showAll, allOption]);

  useEffect(() => {
    if (!open) {
      setOptions(showAll ? [allOption] : []);
    }
  }, [open, showAll, allOption]);

  useEffect(() => {
    let cancelled = false;

    if (showAll && (defaultId == null || defaultId === '')) {
      setSelectedOption(allOption);
      return undefined;
    }

    if (defaultId == null || defaultId === '') {
      setSelectedOption(null);
      return undefined;
    }

    if (typeof defaultValue === 'object' && defaultValue != null && defaultValue.name) {
      setSelectedOption(defaultValue);
      return undefined;
    }

    (async () => {
      try {
        const response = await getShipmentType(defaultId);
        if (!cancelled) {
          setSelectedOption(response?.data || null);
        }
      } catch (err) {
        console.error('ShipmentTypeOptionList resolve default:', err);
        if (!cancelled) {
          setSelectedOption({ id: defaultId, name: String(defaultId) });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [defaultValue, defaultId, showAll, allOption]);

  const handleChange = (event, newOption) => {
    setSelectedOption(newOption);

    if (newOption == null) {
      if (showAll) {
        setSelectedOption(allOption);
        onChange(event, '');
      } else {
        setSelectedOption(null);
        onChange(event, null);
      }
      return;
    }

    if (isAllOption(newOption)) {
      onChange(event, '');
      return;
    }

    if (showAll) {
      onChange(event, newOption.id);
      return;
    }

    onChange(event, newOption);
  };

  const autocompleteValue = useMemo(() => {
    if (selectedOption != null) {
      return selectedOption;
    }
    if (showAll && (defaultId == null || defaultId === '')) {
      return allOption;
    }
    return null;
  }, [selectedOption, showAll, defaultId, allOption]);

  return (
    <Box
      sx={{
        marginBottom: slotProps.root?.sx?.marginBottom ?? 2,
        position: 'relative',
        '&:focus-within': {
          boxShadow: '0 0 8px rgba(0, 128, 255, 0.3)',
          borderRadius: '8px',
        },
        ...(slotProps.root?.sx || {}),
      }}
    >
      <FormLabel
        sx={{
          fontWeight: 600,
          color: errorMessage ? 'danger.500' : '#555',
          marginBottom: '6px',
          fontSize: '14px',
          ...(slotProps.label?.sx || {}),
        }}
      >
        {required ? `${resolvedLabel} *` : resolvedLabel}
      </FormLabel>
      <Autocomplete
        size={size}
        onInputChange={handleInputChange}
        value={autocompleteValue}
        open={open}
        onOpen={() => {
          setOpen(true);
          fetchTypes(inputValue || '');
        }}
        onClose={() => setOpen(false)}
        placeholder={
          !canListShipmentTypes
            ? t('permissions.noPermission', {
                defaultValue: 'You do not have permission to perform this action.',
              })
            : t('widgets.typeToSearch')
        }
        options={Array.isArray(options) ? options : []}
        loading={loading}
        disabled={disabled || !canListShipmentTypes}
        onChange={handleChange}
        isOptionEqualToValue={(option, value) => {
          if (isAllOption(option) && isAllOption(value)) return true;
          return option?.id === value?.id;
        }}
        getOptionLabel={(option) => labelForType(option)}
        noOptionsText={
          !canListShipmentTypes ? (
            <span>
              {t('permissions.noPermission', {
                defaultValue: 'You do not have permission to perform this action.',
              })}
            </span>
          ) : (
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              width="100%"
              px={2}
            >
              <span>{t('widgets.typeToSearch')}</span>
              <Button
                variant="solid"
                color="primary"
                size="sm"
                onClick={() => window.open('/settings/shipment-types', '_blank')}
              >
                {t('widgets.addShipmentType')}
              </Button>
            </Box>
          )
        }
        loadingText={t('actions.loading')}
        filterOptions={(opts) => opts}
        endDecorator={
          loading && (
            <CircularProgress size="sm" sx={{ bgcolor: 'background.surface' }} />
          )
        }
        sx={{
          backgroundColor: '#f7f9fc',
          borderRadius: '8px',
          padding: '4px',
          boxShadow: errorMessage
            ? '0 0 0 1px var(--joy-palette-danger-500, #c41c1c)'
            : '0 2px 4px rgba(0, 0, 0, 0.1)',
          '&:hover': {
            borderColor: errorMessage ? undefined : '#5f09c2',
          },
          '&:focus': {
            borderColor: errorMessage ? undefined : '#5f09c2',
            outline: 'none',
            boxShadow: errorMessage
              ? '0 0 0 1px var(--joy-palette-danger-500, #c41c1c)'
              : '0 0 6px rgba(0, 123, 255, 0.5)',
          },
          '&[disabled]': {
            backgroundColor: '#e9ecef',
            color: '#6c757d',
            cursor: 'not-allowed',
          },
          ...sxProp,
        }}
      />
      {errorMessage ? (
        <FormHelperText sx={{ color: 'danger.500', mt: 0.5 }}>{errorMessage}</FormHelperText>
      ) : null}
    </Box>
  );
}
